import { Alert, Modal, Select, Spin, Tag } from "antd";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../variables/api/axiosInstance";

const EXPERTS_ENDPOINT = "/api/expertise/expert/all";
const ASSIGN_ENDPOINT = (codeId, victimeId) => `/api/sinistre/expert/codeId/${codeId}/${victimeId}`;

const typeLabelFromExpert = (expert) => {
  return (
    expert?.typeExpert?.libelle ||
    expert?.typeExpert?.label ||
    expert?.type ||
    expert?.profession ||
    expert?.categorie?.libelle ||
    expert?.categorie ||
    expert?.specialite ||
    "Autre"
  );
};

const nameLabelFromExpert = (expert) => {
  const nom = expert?.nom || "";
  const prenom = expert?.prenom || "";
  const raison = expert?.raisonSociale || "";
  const computed = `${nom}${prenom ? ` ${prenom}` : ""}`.trim();
  return computed || raison || expert?.displayName || "Expert";
};

const AssignExpertsModal = ({
  open,
  sinistre,
  assignedExperts,
  onClose,
  onSuccess,
}) => {
  const [availableExperts, setAvailableExperts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [typeFilter, setTypeFilter] = useState(null);
  const [selectedVictime, setSelectedVictime] = useState("");

  const sinistreCodeId = sinistre?.codeId;

  useEffect(() => {
    if (open) {
      const preset = (assignedExperts || []).map((expert) => expert.codeId);
      setSelectedIds(preset);
      setTypeFilter(null);
      fetchExperts();
    }
  }, [open, assignedExperts]);

  const fetchExperts = () => {
    setLoading(true);
    axiosInstance
      .get(EXPERTS_ENDPOINT)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setAvailableExperts(list);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des experts");
      })
      .finally(() => setLoading(false));
  };

  const handleAssign = async () => {
    if (!sinistreCodeId) {
      toast.error("Identifiant du sinistre introuvable");
      return;
    }

    const selectedExperts = availableExperts.filter((expert) =>
      selectedIds.includes(expert.codeId)
    );

    const toAdd = selectedExperts.filter(
      (expert) => !(assignedExperts || []).some((a) => a.codeId === expert.codeId)
    );

    if (toAdd.length === 0) {
      toast.info("Aucun nouvel expert à affecter");
      onClose?.();
      return;
    }

    setSaving(true);
    try {
      await Promise.all(
        toAdd.map((expert) => axiosInstance.post(ASSIGN_ENDPOINT(sinistreCodeId, selectedVictime), expert))
      );
      toast.success("Experts affectés avec succès");
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'affectation des experts");
    } finally {
      setSaving(false);
    }
  };

  const typeOptions = useMemo(() => {
    const unique = new Set(
      availableExperts
        .map((expert) => typeLabelFromExpert(expert))
        .filter(Boolean)
    );
    return Array.from(unique)
      .sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }))
      .map((label) => ({ value: label, label }));
  }, [availableExperts]);

  const filteredExperts = useMemo(() => {
    if (!typeFilter) {
      return [];
    }
    return availableExperts.filter(
      (expert) => typeLabelFromExpert(expert) === typeFilter
    );
  }, [availableExperts, typeFilter]);

  const options = useMemo(() => {
    const base = filteredExperts.map((expert) => ({
      value: expert.codeId,
      label: nameLabelFromExpert(expert),
      _type: typeLabelFromExpert(expert),
    }));

    const selectedButHidden = availableExperts.filter(
      (expert) =>
        selectedIds.includes(expert.codeId) &&
        !filteredExperts.some((item) => item.codeId === expert.codeId)
    );

    selectedButHidden.forEach((expert) => {
      base.push({
        value: expert.codeId,
        label: `${nameLabelFromExpert(expert)} (hors filtre)`,
        _type: typeLabelFromExpert(expert),
      });
    });

    return base.sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }));
  }, [availableExperts, filteredExperts, selectedIds]);

  const tagRender = (props) => {
    const { label, value, onClose } = props;
    const option = options.find((item) => item.value === value);
    const type = option?._type;

    return (
      <Tag closable onClose={onClose} style={{ marginRight: 4 }}>
        {label}
        {type ? <span style={{ opacity: 0.7 }}> — {type}</span> : null}
      </Tag>
    );
  };

  return (
    <Modal
      title="Affecter des experts"
      centered
      open={open}
      onCancel={onClose}
      onOk={handleAssign}
      okText="Enregistrer"
      cancelText="Annuler"
      confirmLoading={saving}
      destroyOnClose
    >
      {loading ? (
        <div className="text-center" style={{ padding: "24px 0" }}>
          <Spin size="large" />
        </div>
      ) : availableExperts.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="Aucun expert disponible"
          description="Veuillez créer des experts depuis les paramètres de sinistre."
        />
      ) : (
        <>
          <p className="text-muted" style={{ marginBottom: 8 }}>
            Sélectionnez les experts à affecter au dossier. Filtrez par type pour
            afficher les profils souhaités.
          </p>

          <Select
            allowClear
            style={{ width: "100%", marginBottom: 12 }}
            placeholder="Choisir un type d'expert (ex. Automobile, Médical, etc.)"
            options={typeOptions}
            value={typeFilter || undefined}
            onChange={(value) => setTypeFilter(value ?? null)}
            showSearch
            optionFilterProp="label"
          />

          {!typeFilter ? (
            <Alert
              type="info"
              showIcon
              message="Filtrer par type"
              description="Veuillez sélectionner un type pour afficher et sélectionner les experts correspondants."
              style={{ marginBottom: 12 }}
            />
          ) : null}

          <Select
            mode="multiple"
            style={{ width: "100%" }}
            options={options}
            value={selectedIds}
            onChange={setSelectedIds}
            placeholder={
              typeFilter
                ? "Choisir un ou plusieurs experts"
                : "Sélectionnez d'abord un type d'expert"
            }
            optionFilterProp="label"
            showSearch
            tagRender={tagRender}
            disabled={!typeFilter}
          />
          <br />
          <label className="form-label">Sélectionnez la victime à expertiser</label>
          <Select
            style={{ width: "100%" }}
            options={sinistre?.victimes.map((victime) => ({
              value: victime.codeId,
              label: `${victime?.personne?.prenom} ${victime?.personne?.nom}`,
            }))}
            value={selectedVictime}
            onChange={setSelectedVictime}
            placeholder="Sélectionnez la victime à expertiser"
            optionFilterProp="label"
            showSearch
          />
        </>
      )}
    </Modal>
  );
};

AssignExpertsModal.propTypes = {
  open: PropTypes.bool,
  sinistre: PropTypes.object,
  assignedExperts: PropTypes.array,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

AssignExpertsModal.defaultProps = {
  assignedExperts: [],
};

export default AssignExpertsModal;
